/**
 * HelpButton - Main help button for headers and navigation
 *
 * Clicking opens the sliding help panel on the right side.
 *
 * @example
 * // In header (opens help for current page)
 * <HelpButton />
 *
 * @example
 * // With specific content
 * <HelpButton contentId="training:library" />
 */

import { HelpCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useHelp } from './useHelp';
import { cn } from '@/lib/utils';

interface HelpButtonProps {
  /** Specific content ID to show (e.g., "training:library"). If not provided, shows help for current page. */
  contentId?: string;
  /** Button variant: "icon" (default), "text", or "subtle" */
  variant?: 'icon' | 'text' | 'subtle';
  /** Size: "default", "sm", or "lg" */
  size?: 'default' | 'sm' | 'lg';
  /** Custom tooltip text */
  tooltip?: string;
  /** Additional CSS classes */
  className?: string;
}

export function HelpButton({
  contentId,
  variant = 'icon',
  size = 'default',
  tooltip = 'Get help (press ?)',
  className,
}: HelpButtonProps) {
  const { openHelp } = useHelp();

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    openHelp(contentId);
  };

  const iconSize = {
    default: 'h-5 w-5',
    sm: 'h-4 w-4',
    lg: 'h-6 w-6',
  }[size];

  const buttonSize = {
    default: 'h-9 w-9',
    sm: 'h-7 w-7',
    lg: 'h-11 w-11',
  }[size];

  if (variant === 'subtle') {
    return (
      <button
        type="button"
        onClick={handleClick}
        title={tooltip}
        className={cn(
          'inline-flex items-center justify-center rounded-full text-muted-foreground hover:text-muted-foreground hover:bg-muted transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1',
          size === 'sm' && 'h-5 w-5',
          size === 'default' && 'h-6 w-6',
          size === 'lg' && 'h-7 w-7',
          className
        )}
        aria-label={tooltip}
      >
        <HelpCircle className={cn(
          size === 'sm' && 'h-3.5 w-3.5',
          size === 'default' && 'h-4 w-4',
          size === 'lg' && 'h-5 w-5',
        )} />
      </button>
    );
  }

  if (variant === 'text') {
    return (
      <Button
        variant="ghost"
        size={size === 'lg' ? 'default' : size === 'sm' ? 'sm' : 'default'}
        onClick={handleClick}
        title={tooltip}
        className={cn('gap-1.5', className)}
      >
        <HelpCircle className={iconSize} />
        <span>Help</span>
      </Button>
    );
  }

  // Default: icon variant
  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={handleClick}
      title={tooltip}
      className={cn(buttonSize, 'text-muted-foreground hover:text-primary-600', className)}
      aria-label={tooltip}
    >
      <HelpCircle className={iconSize} />
    </Button>
  );
}
